import * as THREE from 'three';
import type { RfimsModelPlacement } from './modelPlacement';

export const RFIMS_REFERENCE_COLUMNS = 22;
export const RFIMS_ORIENTATION_NOTE = '22-column array reference; the highlighted reference column faces true north. Installed column numbering and measured alignment have not been verified.';

/** Exterior reference from NOAA 2021 p.8 / NTIA 2023 p.14 photographs.
 * Shape is evidenced; dimensions, current configuration and orientation are illustrative.
 * No invented receiver internals or component telemetry bindings are attached. */
export function buildRfimsArrayReference(support: RfimsModelPlacement['support'] = 'trailer'): THREE.Group {
  const root = new THREE.Group();
  root.name = 'rfims-equipment-reference';
  root.userData.provenance = 'Historical photograph reference; illustrative dimensions';
  const part = (name: string) => { const group = new THREE.Group(); group.name = name; root.add(group); return group; };
  const supportGroup = part('rfims-support'), mastGroup = part('rfims-mast'), arrayGroup = part('rfims-array-shell');
  const cabinets = support === 'trailer' ? part('rfims-cabinets') : null;
  let parent = supportGroup;
  const metal = new THREE.MeshStandardMaterial({ color: '#929d9f', metalness: 0.35, roughness: 0.65 });
  const shell = new THREE.MeshStandardMaterial({ color: '#d5d9ca', metalness: 0.12, roughness: 0.7 });
  const dark = new THREE.MeshStandardMaterial({ color: '#253137', roughness: 0.85 });
  const add = (geometry: THREE.BufferGeometry, material: THREE.Material, at: number[], target = parent) => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(at[0], at[1], at[2]); mesh.castShadow = true; mesh.receiveShadow = true;
    target.add(mesh); return mesh;
  };
  const bar = (a: number[], b: number[], radius = 0.035) => {
    const from = new THREE.Vector3(...a), to = new THREE.Vector3(...b), d = to.clone().sub(from);
    const mesh = add(new THREE.CylinderGeometry(radius, radius, d.length(), 6), metal, from.add(to).multiplyScalar(0.5).toArray());
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
  };
  if (support === 'trailer') {
  add(new THREE.BoxGeometry(2.4, 0.25, 4.5), metal, [0, 0.8, 0.7]);
  for (const x of [-1.3, 1.3]) for (const z of [0.6, 1.7]) {
    const wheel = add(new THREE.CylinderGeometry(0.42, 0.42, 0.25, 18), dark, [x, 0.45, z]); wheel.rotation.z = Math.PI / 2;
  }
  add(new THREE.BoxGeometry(0.9, 1.4, 1.15), shell, [-0.65, 1.6, 1.7], cabinets!);
  add(new THREE.BoxGeometry(0.8, 1.15, 0.75), shell, [0.7, 1.48, 1.85], cabinets!);
  // Trailer rails, ventilated enclosure and cable runs visible in the public
  // NOAA GSAW 2020 platform photographs; all dimensions remain illustrative.
  for (const x of [-1.15, 1.15]) {
    bar([x, 1.25, -1.5], [x, 1.25, 2.8], 0.025);
    for (const z of [-1.5, 0.6, 2.8]) bar([x, 0.9, z], [x, 1.25, z], 0.025);
    bar([x, 0.8, -1.5], [0, 0.8, -3.25], 0.055);
  }
  add(new THREE.BoxGeometry(0.24, 0.18, 0.5), metal, [0, 0.8, -3.3]);
  for (let y = 1.1; y < 2.1; y += 0.1) add(new THREE.BoxGeometry(0.68, 0.025, 0.035), dark, [-0.65, y, 2.29], cabinets!);
  const cable = new THREE.CatmullRomCurve3([new THREE.Vector3(0.15, 9.2, 0.45), new THREE.Vector3(0.25, 6, 0.5), new THREE.Vector3(0.4, 2, 0.55), new THREE.Vector3(0.7, 1.95, 1.5)]);
  add(new THREE.TubeGeometry(cable, 32, 0.035, 6, false), dark, [0, 0, 0]);
  for (const x of [-1, 1]) for (const z of [-1, 1]) {
    bar([x, 0.75, z + 0.7], [x * 2.7, 0.35, z * 2.3 + 0.7], 0.07);
    bar([x * 2.7, 0.1, z * 2.3 + 0.7], [x * 2.7, 0.6, z * 2.3 + 0.7], 0.06);
    add(new THREE.BoxGeometry(0.4, 0.08, 0.4), dark, [x * 2.7, 0.05, z * 2.3 + 0.7]);
  }
  } else if (support === 'pads') {
    // Aerially inferred four-pad support layout, not a trailer or verified foundation plan.
    const concrete = new THREE.MeshStandardMaterial({ color: '#b9b8a6', roughness: 0.95 });
    add(new THREE.BoxGeometry(1.1, 1, 1.1), concrete, [0, 0.5, 0]);
    for (const x of [-3.5, 3.5]) for (const z of [-6, 6]) {
      add(new THREE.BoxGeometry(1.2, 0.28, 3), concrete, [x, 0.14, z]);
      bar([0, 8.7, 0], [x, 0.3, z], 0.012);
    }
  } else {
    // Representative rooftop support only; no ground pads, trailer or invented cabinets.
    add(new THREE.BoxGeometry(1.1, 0.18, 1.1), metal, [0, 0.09, 0]);
    for (const x of [-0.44, 0.44]) for (const z of [-0.44, 0.44]) {
      bar([x, 0.18, z], [x * 0.82, 1, z * 0.82], 0.045);
    }
  }
  parent = mastGroup;
  const mastTop = support === 'roof' ? 3.2 : 9.2;
  const corners = [[-0.36, -0.36], [0.36, -0.36], [0.36, 0.36], [-0.36, 0.36]];
  for (let i = 0; i < 4; i++) {
    const a = corners[i], b = corners[(i + 1) % 4];
    bar([a[0], 1, a[1]], [a[0], mastTop, a[1]], 0.045);
    for (let y = 1; y < mastTop; y += 0.8) {
      const upper = Math.min(y + 0.8, mastTop);
      bar([a[0], y, a[1]], [b[0], upper, b[1]], 0.022);
      bar([b[0], y, b[1]], [a[0], upper, a[1]], 0.022);
    }
    if (support === 'trailer') bar([a[0], 8.7, a[1]], [a[0] * 7, 0.35, a[1] * 7], 0.008);
  }
  parent = arrayGroup;
  add(new THREE.CylinderGeometry(0.8, 0.8, 2.6, 66), shell, [0, mastTop + 1.3, 0]);
  add(new THREE.CylinderGeometry(0.82, 0.82, 0.15, 66), dark, [0, mastTop - 0.05, 0]);
  // Column centres start at -Z (true north in the facility frame), clockwise from above.
  // These schematic strips do not identify the installed manufacturer's column numbers.
  const northMaterial = new THREE.MeshBasicMaterial({color:'#76d4de'});
  for (let i = 0; i < RFIMS_REFERENCE_COLUMNS; i++) {
    const a = i * 2 * Math.PI / RFIMS_REFERENCE_COLUMNS;
    const column = add(new THREE.BoxGeometry(0.035, 2.54, 0.014), i === 0 ? northMaterial : metal,
      [0.807 * Math.sin(a), mastTop + 1.3, -0.807 * Math.cos(a)]);
    column.rotation.y = -a;
    column.name = i === 0 ? 'rfims-north-reference' : `rfims-column-reference-${i}`;
    column.userData.referenceOnly = true;
  }
  arrayGroup.userData.orientation = RFIMS_ORIENTATION_NOTE;
  bar([0, mastTop + 2.6, 0], [0, mastTop + 3.4, 0], 0.012);
  return root;
}
